// Live Test State — the single source of truth for "is a run in flight?"
// -----------------------------------------------------------------------
// Historically the FE answered "is a test running right now?" from three
// or four competing signals — a `testRunning` boolean written by four
// different code paths (keyed variously off the test's endTime vs its
// testStatus), a separate `inFlightTestStatus` field from the dashboard
// preload, and ad-hoc `tests.some(IN_FLIGHT_TEST_STATUSES)` scans inlined
// in the Dashboard and Applications pages. They could disagree — a
// testStatus-driven event could flip the boolean "terminal" while the
// endTime-authoritative truth still said "running" — which is exactly how
// a stuck test showed "Completed" on one surface and "Testing" on another.
//
// Consolidation contract:
//   * The live-run signal is stored in exactly ONE place —
//     WebApplicationClass.testActivity — written through exactly ONE
//     setter, WebApplicationClass.setTestActivity().
//   * Every surface that needs "is a run in flight, and in what phase?"
//     reads it through deriveLiveTestState() below and NOTHING else.
//
// This module answers only "is a run in flight right now?" (Q1). The
// orthogonal question "what was the last finished outcome?" (Q2) is owned
// by getTestResultLabel() in testStatusLabel.ts and is intentionally not
// duplicated here.

import type WebApplicationClass from "@/models/webApplication";
import {
  TEST_STATUS_PROVISIONING,
  TEST_STATUS_RUNNING,
  TEST_STATUS_STOPPING,
  TERMINAL_TEST_STATUSES,
} from "@/constants/definitions";

// Coarse lifecycle phase of the in-flight run. "none" means no run is in
// flight. Kept as a small closed union so UI surfaces switch on it
// exhaustively rather than string-matching raw testStatus values.
export type LivePhase = "provisioning" | "running" | "stopping" | "none";

// The one normalized record of an in-flight run. Null when no run is live.
// Intentionally minimal — the rich test data (vulns, statistics, history)
// lives in WebApplicationClass.tests; this carries only what "is it
// running and in what phase" needs.
export interface TestActivity {
  // The in-flight test's id. Null in the transitional windows where we
  // know a run is live but not yet its id: the optimiztic pre-backend
  // window in startTest (before the BE assigns a testid), and the
  // endTime-authoritative reconcile in isTestRunning (which returns a
  // bare boolean, no id). The next subscription / resync event fills it.
  testId: string | null;
  // Always one of the in-flight statuses (Provisioning / Running /
  // Stopping). setTestActivity refuses to store a terminal status —
  // terminal transitions clear the activity to null instead — so callers
  // can hand it a raw payload without pre-filtering.
  testStatus: string;
  // Unix epoch seconds when the run started, when known; else null.
  startTime: number | null;
  // True when the phase above is a GUESS rather than something the backend
  // reported. Exactly one writer sets it: the endTime-authoritative
  // reconcile in isTestRunning(), whose backend answer is a bare boolean —
  // "a run exists" — from which we invent Running because we cannot tell
  // Provisioning from Running from it.
  //
  // It exists because setTestActivity() otherwise refuses to move a run's
  // phase backwards (see the monotonic rule there). Refining an invented
  // Running down to the real Provisioning is a correction, not a rewind, so
  // a placeholder phase may be replaced by anything. Absent / false means
  // the backend told us this phase and it only moves forward.
  phaseIsPlaceholder?: boolean;
}

// Map a raw testStatus to the coarse phase. Any non-in-flight (terminal,
// null, unknown) status is "none" — callers treat "none" as "not running".
export function phaseOf(status: string | null | undefined): LivePhase {
  switch (status) {
    case TEST_STATUS_PROVISIONING:
      return "provisioning";
    case TEST_STATUS_RUNNING:
      return "running";
    case TEST_STATUS_STOPPING:
      return "stopping";
    default:
      return "none";
  }
}

export interface LiveTestState {
  isRunning: boolean;
  phase: LivePhase;
  startTime: number | null;
}

const NOT_RUNNING: LiveTestState = {
  isRunning: false,
  phase: "none",
  startTime: null,
};

// THE answer to "is a run in flight on this webapp right now?".
// Reads only webapp.testActivity. The TERMINAL guard is belt-and-braces:
// setTestActivity already refuses to store a terminal status, but guarding
// here means a hand-mutated field can never read as "running".
export function deriveLiveTestState(app: WebApplicationClass): LiveTestState {
  const activity = app.testActivity;
  if (
    activity &&
    activity.testStatus &&
    !TERMINAL_TEST_STATUSES.has(activity.testStatus)
  ) {
    return {
      isRunning: true,
      phase: phaseOf(activity.testStatus),
      startTime: activity.startTime,
    };
  }
  return NOT_RUNNING;
}

// Convenience boolean for the many call sites that only need yes/no.
export function isWebAppTestRunning(app: WebApplicationClass): boolean {
  return deriveLiveTestState(app).isRunning;
}
